// src/utils/jobInvoiceGuard.js
// JOBINV VERIFIER: jobinvguard-5e1b7c84
//
// "Has this job already been invoiced?" — founder decision (Marcus, 2026-10-04): WARN, don't
// block. Staged / progress billing is legitimate (an interim invoice drops flat fees, and labour
// only ever bills the hours since the last invoice — laborBilling.priorBilledFromInvoices), but a
// second final invoice by accident bills every flat line item twice. Census 2026-10-04: test org
// 4 jobs with 2+ invoices (e.g. 3 × $146.90), admin_hq 1.
//
// Two halves, both used by the web single-job path (CrmApp.handleGenerateInvoiceFromJob →
// InvoiceFormModal), which used to have neither:
//   1. the WARNING — mirrors the estimate flow (CrmApp.handleConvertToInvoice, seamE-6c4a7f92):
//      look up the job's invoices first; if any exist, confirm "create another?";
//   2. the MARKER — job.invoiceId, the field every "Needs Invoicing" view reads
//      (Completed && !invoiceId: JobManagementView, HomeView, mobile OwnerHomeScreen) and that
//      batchCreateInvoices refuses ("Already invoiced"). completeJob and batchCreateInvoices
//      always set it; the web path never did, so a hand-invoiced job stayed in the queue and
//      batch could bill it again.

// Invoice numbers already on this job, from the two link fields invoices carry (linkedJobId on
// every path; jobId on older completeJob invoices) plus the job's own marker. De-duplicated,
// oldest first; an invoice with no number falls back to a short id (same as the estimate flow).
export const collectPriorJobInvoices = ({ job, invoiceDocs }) => {
    const seen = new Map();
    (Array.isArray(invoiceDocs) ? invoiceDocs : []).forEach((d) => {
        if (!d || !d.id || seen.has(d.id)) return;
        const created = d.createdAt && typeof d.createdAt.toMillis === 'function' ? d.createdAt.toMillis()
            : (d.createdAt instanceof Date ? d.createdAt.getTime() : 0);
        seen.set(d.id, { id: d.id, number: d.invoiceNumber || d.id.slice(0, 6), created });
    });
    if (job && job.invoiceId && !seen.has(job.invoiceId)) {
        seen.set(job.invoiceId, { id: job.invoiceId, number: job.invoiceId.slice(0, 6), created: 0 });
    }
    return [...seen.values()].sort((a, b) => a.created - b.created);
};

// The confirmation wording — the estimate dialog's sentence with "job" for the document word.
export const jobInvoiceWarning = (priorNumbers) => ({
    title: 'This job already has an invoice',
    message: `You already created invoice #${priorNumbers.join(', ')} from this job. Creating another makes a second, separate invoice — if you didn't mean to, this bills the customer twice for the same work.`,
    confirmText: 'Create another invoice',
});

// What to write to the job when an invoice is created from it. Only a COMPLETED job is marked, so
// a progress invoice on an unfinished job leaves it out of "invoiced" and the job still reaches
// Needs Invoicing when it is finished (staged billing keeps working). An existing marker is never
// overwritten — it keeps pointing at the job's first final invoice. Returns the fields to set, or
// null when nothing should change.
export const jobInvoiceMarker = ({ jobData, newInvoiceId }) => {
    if (!jobData || !newInvoiceId) return null;
    if (jobData.status !== 'Completed') return null;
    if (jobData.invoiceId) return null;
    return { invoiceId: newInvoiceId };
};
